// Which piece a blunder gave away, read from the engine's reply stored in the
// scan: the position after the blunder, its best line, played out on a bare
// board. No chess library: only captures matter, so a move is "from square to
// square", with en passant and promotion handled.
//
// The rule: play the line's first `LINE_PLIES` plies, adding up the material
// each side captures. The reader's net loss is what they gave away; the piece
// named is the most valuable one of theirs the opponent took that the net
// loss covers, so an exchange of queens before a knight falls names the
// knight; a queen given for a rook names the queen. A forced mate against the reader is `mate`. Under a pawn's net
// loss, the blunder cost position rather than material: no piece.
import { type Color, type Line, type Score, type LostPiece } from '@chess/api-model';

/** How many plies of the engine's reply are played out. */
export const LINE_PLIES = 6;

type Piece = Exclude<LostPiece, 'mate'> | 'king';
const VALUE: Record<Piece, number> = { pawn: 1, knight: 3, bishop: 3, rook: 5, queen: 9, king: 0 };
const BY_LETTER: Record<string, Piece> = { p: 'pawn', n: 'knight', b: 'bishop', r: 'rook', q: 'queen', k: 'king' };

interface Square {
  piece: Piece;
  colour: Color;
}

function board(fen: string): Map<string, Square> {
  const squares = new Map<string, Square>();
  fen
    .split(' ')[0]
    .split('/')
    .forEach((row, r) => {
      let file = 0;
      for (const ch of row) {
        const empty = Number(ch);
        if (Number.isInteger(empty)) {
          file += empty;
          continue;
        }
        const name = `${'abcdefgh'[file]}${8 - r}`;
        squares.set(name, { piece: BY_LETTER[ch.toLowerCase()], colour: ch === ch.toUpperCase() ? 'white' : 'black' });
        file += 1;
      }
    });
  return squares;
}

/**
 * The piece the reader's blunder gave away. `fen` is the position after the
 * blunder, the opponent to move; `line` the engine's best line from it, and
 * `score` that position's score, for the side to move.
 */
export function lostPieceOf(fen: string, line: Pick<Line, 'pv'> | undefined, score: Score | undefined, reader: Color): LostPiece | undefined {
  // The score is the opponent's: a mate for them is a mate against the reader.
  if (score?.mate !== undefined && score.mate > 0) return 'mate';
  if (!line?.pv.length) return undefined;
  const squares = board(fen);
  let net = 0;
  const taken: Piece[] = [];
  for (const uci of line.pv.slice(0, LINE_PLIES)) {
    const from = uci.slice(0, 2);
    const to = uci.slice(2, 4);
    const mover = squares.get(from);
    if (!mover) break;
    let captured = squares.get(to);
    // En passant: a pawn moving diagonally onto an empty square takes the pawn beside it.
    if (!captured && mover.piece === 'pawn' && from[0] !== to[0]) {
      const beside = `${to[0]}${from[1]}`;
      captured = squares.get(beside);
      squares.delete(beside);
    }
    if (captured) {
      if (captured.colour === reader) {
        net -= VALUE[captured.piece];
        taken.push(captured.piece);
      } else {
        net += VALUE[captured.piece];
      }
    }
    squares.delete(from);
    const promotion = uci[4] ? BY_LETTER[uci[4]] : undefined;
    squares.set(to, promotion ? { piece: promotion, colour: mover.colour } : mover);
  }
  const loss = -net;
  if (loss < 1) return undefined;
  const pieces = taken.filter((p): p is Exclude<Piece, 'king'> => p !== 'king');
  const covered = pieces.filter((p) => VALUE[p] <= loss).sort((a, b) => VALUE[b] - VALUE[a])[0];
  // A piece for less (a queen for a rook) loses more than any one piece is worth: name the cheapest one taken.
  return covered ?? pieces.sort((a, b) => VALUE[a] - VALUE[b])[0];
}
